import { useMemo, useState } from 'react';
import { Alert, App, Button, Card, Checkbox, DatePicker, Image, Segmented, Select, Space, Tooltip, Typography } from 'antd';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/DataTable';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import {
  Camera,
  CheckCircle,
  DownloadSimple,
  LockKey,
  Prohibit,
  WarningCircle,
} from '@phosphor-icons/react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { HintBanner, HintToggle, useDismissibleHint } from '../../components/DismissibleHint';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { isSuperAdmin, useCurrentStaff } from '../../auth/session';
import { catalogApi, catalogKeys } from '../catalog/api';
import { tokens } from '../../theme';
import {
  reconApi,
  reconKeys,
  type BillScanListItem,
  type BillScanListFilters,
  type PendingMemberScan,
  type ReversalListItem,
  type ReversalStatus,
} from './api';
import { BILL_STATUS, billStatusMeta, flagMeta, visibleFlags } from './flagLabels';
import { BillScanDetailModal } from './BillScanDetailModal';
import { RejectReversalModal } from './RejectReversalModal';
import { RejectScanModal } from './RejectScanModal';
import { VND } from '../../format';

const BILL_SCAN_PAGE_SIZE = 10;
const REVERSAL_PAGE_SIZE = 5;

function formatVnDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}/${get('month')} ${get('hour')}:${get('minute')}`;
}

const REVERSAL_STATES: Record<ReversalStatus, string> = {
  Pending: 'Chờ duyệt',
  Approved: 'Đã duyệt',
  Rejected: 'Đã từ chối',
};

function Tile({
  label,
  value,
  hint,
  danger,
  muted,
  tooltip,
}: {
  label: string;
  value: string;
  hint: string;
  danger?: boolean;
  muted?: boolean;
  tooltip?: string;
}) {
  const body = (
    <div
      style={{
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: tokens.radius.card,
        padding: tokens.space.cardPadding,
        boxShadow: tokens.shadow.card,
        opacity: muted ? 0.65 : 1,
        height: '100%',
      }}
    >
      <div style={{ fontSize: 12, color: tokens.color.ink2, display: 'flex', alignItems: 'center', gap: 6 }}>
        {danger && (
          <span
            aria-hidden
            style={{ width: 7, height: 7, borderRadius: 999, background: tokens.color.danger, flex: 'none' }}
          />
        )}
        {label}
        {muted && <LockKey size={12} weight="fill" aria-hidden />}
      </div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: danger ? tokens.color.danger : tokens.color.ink,
          letterSpacing: '-0.02em',
          marginTop: 4,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 12, color: tokens.color.ink3, marginTop: 4 }}>{hint}</div>
    </div>
  );
  return tooltip ? (
    <Tooltip title={tooltip}>
      <div style={{ height: '100%' }}>{body}</div>
    </Tooltip>
  ) : (
    body
  );
}

export function ReconPage() {
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const staff = useCurrentStaff();
  const hq = isSuperAdmin(staff);
  const canConfirmPending = staff?.role === 'branch_manager';
  const hqQueueHint = useDismissibleHint('recon-hq-no-queue');

  // Bộ lọc chung: MỘT ngày. Đối soát và mọi chỉ số ở màn này đều theo từng ngày — chọn khoảng
  // ngày làm số bị gộp mà giao diện không tách ra được, nên chỉ cho chọn một ngày.
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [branchId, setBranchId] = useState<string>('all');
  const [scanSource, setScanSource] = useState<'all' | 'Staff' | 'Member'>('all');
  const [scanStatus, setScanStatus] = useState<string | undefined>(undefined);
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  // Phân trang con trỏ cho danh sách hóa đơn quét.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const resetPaging = () => {
    setCursorStack([null]);
    setPageIndex(0);
  };

  const [reversalFilter, setReversalFilter] = useState<'Pending' | 'all'>('Pending');
  // Từ chối dùng chung cho bảng chờ xác nhận và bảng danh sách — chỉ cần id + mã hóa đơn.
  const [rejectingScan, setRejectingScan] = useState<{ id: string; billRef: string } | null>(null);
  const [rejectingReversal, setRejectingReversal] = useState<ReversalListItem | null>(null);
  const [detailScan, setDetailScan] = useState<BillScanListItem | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [csvDownloading, setCsvDownloading] = useState(false);

  // Một ngày: from và to bằng nhau. Giữ hai biến để không phải sửa các query dùng khoảng.
  const from = date.format('YYYY-MM-DD');
  const to = from;
  const branchScope = branchId === 'all' ? undefined : branchId;

  const branchesQuery = useQuery({ queryKey: catalogKeys.branches, queryFn: catalogApi.listBranches });
  const branchName = branchesQuery.data?.find((b) => b.id === branchId)?.name;

  const statsQuery = useQuery({
    queryKey: reconKeys.stats(from, to, branchScope),
    queryFn: () => reconApi.stats(from, to, branchScope),
    refetchInterval: 30_000,
  });

  // Đối soát cuối ngày (S7-BE-01) — theo ngày đang chọn.
  const dayEndQuery = useQuery({
    queryKey: reconKeys.dayEnd(to, branchScope),
    queryFn: () => reconApi.dayEnd(to, branchScope),
    refetchInterval: 30_000,
  });

  const billScanFilters: BillScanListFilters = {
    branchIds: branchScope ? [branchScope] : undefined,
    from,
    to,
    status: scanStatus,
    source: scanSource === 'all' ? undefined : scanSource,
    hasFlags: onlyFlagged || undefined,
  };
  const currentCursor = cursorStack[pageIndex];
  const billScansQuery = useQuery({
    queryKey: reconKeys.billScans(billScanFilters, currentCursor),
    queryFn: () => reconApi.listBillScans(billScanFilters, currentCursor, BILL_SCAN_PAGE_SIZE),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  const pendingQuery = useQuery({
    queryKey: reconKeys.pending,
    queryFn: reconApi.listPending,
    enabled: canConfirmPending,
    refetchInterval: 30_000,
  });

  const reversalStatus: ReversalStatus | undefined =
    reversalFilter === 'Pending' ? 'Pending' : undefined;
  // Kéo cả tập (trần server 100) một lần rồi để AntD chia trang phía client —
  // hợp với bộ lọc chi nhánh phía client và giữ card luôn gọn.
  const reversalsQuery = useQuery({
    queryKey: reconKeys.reversals(reversalStatus),
    queryFn: () => reconApi.listReversals(reversalStatus, 1, 100),
    refetchInterval: 30_000,
  });

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['recon'] });
  };

  const downloadCsv = async () => {
    setCsvDownloading(true);
    try {
      await reconApi.downloadReconciliationCsv(to, branchScope);
      message.success('Đã tải CSV đối soát.');
    } catch (error) {
      reportError(error, 'Không tải được CSV đối soát.');
    } finally {
      setCsvDownloading(false);
    }
  };

  const decideMutation = useMutation({
    mutationFn: ({ id, approve, note }: { id: string; approve: boolean; note?: string }) =>
      reconApi.decidePending(id, approve, note),
    onSuccess: async (data) => {
      await refresh();
      setRejectingScan(null);
      message.success(
        data.approved
          ? `Đã xác nhận. Cộng ${VND.format(data.pointsAwarded)} điểm.`
          : 'Đã từ chối hóa đơn.',
      );
    },
    onError: (error) => reportError(error, 'Không quyết được hóa đơn.'),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => reconApi.approveReversal(id),
    onSuccess: async () => {
      await refresh();
      message.success('Đã đảo điểm. Sổ cái ghi dòng âm, dòng gốc giữ nguyên.');
    },
    onError: (error) => reportError(error, 'Không duyệt được yêu cầu đảo điểm.'),
  });

  const rejectReversalMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => reconApi.rejectReversal(id, note),
    onSuccess: async () => {
      await refresh();
      setRejectingReversal(null);
      message.success('Đã từ chối yêu cầu đảo điểm.');
    },
    onError: (error) => reportError(error, 'Không từ chối được yêu cầu.'),
  });

  const openImage = async (billScanId: string) => {
    try {
      const image = await reconApi.getImage(billScanId);
      setPreviewUrl(image.url);
    } catch (error) {
      reportError(error, 'Không mở được ảnh hóa đơn.');
    }
  };

  const pending = pendingQuery.data ?? [];
  const reversals = useMemo(() => {
    const items = reversalsQuery.data?.items ?? [];
    if (!branchName) return items;
    return items.filter((row) => row.branchName === branchName);
  }, [reversalsQuery.data, branchName]);

  const pendingReversalCount = reversalsQuery.data?.pendingCount ?? 0;
  const reviewCount = pending.length + pendingReversalCount;
  const stats = statsQuery.data;
  const dayEnd = dayEndQuery.data;

  const billScans = billScansQuery.data?.items ?? [];
  const dateLabel = date.format('DD/MM/YYYY');

  const scanColumns: ColumnsType<PendingMemberScan> = [
    {
      title: 'Thời điểm',
      width: 112,
      responsive: ['md'],
      render: (_, row) => (
        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}>
          {formatVnDateTime(row.scannedAt)}
        </span>
      ),
    },
    {
      title: 'Hóa đơn',
      width: 220,
      render: (_, row) => (
        <>
          <div style={{ fontWeight: 600, fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>
            {row.billRef}
          </div>
          <div style={{ fontSize: 12, color: tokens.color.ink2 }}>
            Khách: {row.memberName || row.memberPhone}
          </div>
        </>
      ),
    },
    {
      title: 'Số tiền',
      align: 'right',
      width: 130,
      render: (_, row) => (
        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {VND.format(row.amount)}đ
        </span>
      ),
    },
    {
      title: 'Lý do đánh dấu',
      width: 240,
      responsive: ['lg'],
      render: (_, row) => <FlagChips flags={row.flags} />,
    },
    {
      title: 'Thao tác',
      width: 220,
      render: (_, row) => (
        <Space size={8} wrap>
          {row.hasImage && (
            <Button
              size="small"
              icon={<Camera size={14} />}
              aria-label={`Xem ảnh hóa đơn ${row.billRef}`}
              onClick={() => void openImage(row.billScanId)}
            >
              Xem ảnh
            </Button>
          )}
          <Button
            size="small"
            type="primary"
            icon={<CheckCircle size={14} />}
            aria-label={`Xác nhận hóa đơn ${row.billRef}`}
            loading={
              decideMutation.isPending &&
              decideMutation.variables?.id === row.billScanId &&
              decideMutation.variables?.approve === true
            }
            onClick={() => decideMutation.mutate({ id: row.billScanId, approve: true })}
          >
            Xác nhận
          </Button>
          <Button
            size="small"
            danger
            icon={<Prohibit size={14} />}
            aria-label={`Từ chối hóa đơn ${row.billRef}`}
            onClick={() => setRejectingScan({ id: row.billScanId, billRef: row.billRef })}
          >
            Từ chối
          </Button>
        </Space>
      ),
    },
  ];

  const billScanColumns: ColumnsType<BillScanListItem> = [
    {
      title: 'Thời điểm',
      width: 108,
      responsive: ['md'],
      render: (_, row) => (
        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}>
          {formatVnDateTime(row.scannedAt)}
        </span>
      ),
    },
    {
      title: 'Hóa đơn',
      width: 220,
      render: (_, row) => (
        <>
          <div style={{ fontWeight: 600, fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>
            {row.billRef}
          </div>
          <div style={{ fontSize: 12, color: tokens.color.ink2 }}>
            Khách: {row.memberName || row.memberCode}
          </div>
        </>
      ),
    },
    {
      title: 'Chi nhánh và nhân viên',
      width: 200,
      responsive: ['lg'],
      render: (_, row) => (
        <>
          <div style={{ fontWeight: 500 }}>{row.branchName}</div>
          <div style={{ fontSize: 12, color: tokens.color.ink3 }}>
            {row.staffName ?? 'Khách tự quét'}
          </div>
        </>
      ),
    },
    {
      title: 'Số tiền',
      align: 'right',
      width: 128,
      render: (_, row) => (
        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {VND.format(row.amount)}đ
        </span>
      ),
    },
    {
      title: 'Điểm',
      align: 'right',
      width: 76,
      responsive: ['sm'],
      render: (_, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{VND.format(row.pointsAwarded)}</span>
      ),
    },
    {
      title: 'Trạng thái',
      width: 128,
      render: (_, row) => {
        const meta = billStatusMeta(row.status);
        return <StatusTag color={meta.color}>{meta.label}</StatusTag>;
      },
    },
    {
      title: 'Lý do đánh dấu',
      width: 220,
      responsive: ['xl'],
      render: (_, row) => <FlagChips flags={row.flags} />,
    },
    {
      title: 'Thao tác',
      width: 230,
      render: (_, row) => (
        <Space size={6} wrap>
          <Button
            size="small"
            aria-label={`Xem chi tiết hóa đơn ${row.billRef}`}
            onClick={() => setDetailScan(row)}
          >
            Chi tiết
          </Button>
          {row.hasImage && (
            <Button
              size="small"
              icon={<Camera size={14} />}
              aria-label={`Xem ảnh hóa đơn ${row.billRef}`}
              onClick={() => void openImage(row.id)}
            >
              Ảnh
            </Button>
          )}
          {row.status === 'PendingConfirmation' && (
            <>
              <Button
                size="small"
                type="primary"
                icon={<CheckCircle size={14} />}
                aria-label={`Xác nhận hóa đơn ${row.billRef}`}
                loading={
                  decideMutation.isPending &&
                  decideMutation.variables?.id === row.id &&
                  decideMutation.variables?.approve === true
                }
                onClick={() => decideMutation.mutate({ id: row.id, approve: true })}
              >
                Xác nhận
              </Button>
              <Button
                size="small"
                danger
                icon={<Prohibit size={14} />}
                aria-label={`Từ chối hóa đơn ${row.billRef}`}
                onClick={() => setRejectingScan({ id: row.id, billRef: row.billRef })}
              >
                Từ chối
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const reversalColumns: ColumnsType<ReversalListItem> = [
    {
      title: 'Khách / hóa đơn',
      width: 280,
      render: (_, row) => (
        <>
          <div style={{ fontWeight: 600 }}>Hoàn {VND.format(row.spendablePoints)} điểm · {row.memberCode}</div>
          <div style={{ fontSize: 12.5, color: tokens.color.ink2, marginTop: 4, lineHeight: 1.55 }}>
            {row.reason}
          </div>
          <div style={{ fontSize: 11.5, color: tokens.color.ink3, fontFamily: 'ui-monospace, monospace', marginTop: 6 }}>
            {row.branchName} · {row.requestedBy} · {formatVnDateTime(row.requestedAt)} · {VND.format(row.billAmount)}đ
          </div>
        </>
      ),
    },
    {
      title: 'Trạng thái',
      width: 120,
      render: (_, row) => (
        <StatusTag color={row.status === 'Pending' ? 'warning' : row.status === 'Approved' ? 'success' : 'default'}>
          {REVERSAL_STATES[row.status]}
        </StatusTag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 220,
      render: (_, row) => {
        if (row.status !== 'Pending') return null;
        if (!hq) {
          return <span style={{ fontSize: 12.5, color: tokens.color.ink3 }}>Chờ hội sở duyệt</span>;
        }
        return (
          <Space wrap>
            <Button
              type="primary"
              aria-label={`Duyệt đảo điểm ${row.memberCode}`}
              loading={approveMutation.isPending && approveMutation.variables === row.id}
              onClick={() => {
                modal.confirm({
                  title: `Duyệt đảo ${VND.format(row.spendablePoints)} điểm?`,
                  content:
                    'Điểm sẽ trừ bằng một dòng sổ cái âm. Dòng tích gốc giữ nguyên. Thao tác không tự hoàn.',
                  okText: 'Duyệt đảo điểm',
                  cancelText: 'Hủy',
                  onOk: () => approveMutation.mutateAsync(row.id),
                });
              }}
            >
              Duyệt đảo điểm
            </Button>
            <Button onClick={() => setRejectingReversal(row)}>Từ chối</Button>
          </Space>
        );
      },
    },
  ];

  const branchOptions = [
    { value: 'all', label: 'Tất cả chi nhánh' },
    ...(branchesQuery.data ?? []).map((b) => ({ value: b.id, label: b.name })),
  ];

  return (
    <>
      <PageHeader
        title="Đối soát cuối ngày"
        subtitle={`Ngày ${dateLabel} · hàng chờ xác nhận và đảo điểm hai cấp`}
        actions={
          <Space size={8} wrap>
            <DatePicker
              value={date}
              onChange={(v) => {
                if (v) {
                  setDate(v);
                  resetPaging();
                }
              }}
              allowClear={false}
              format="DD/MM/YYYY"
              aria-label="Ngày đối soát"
              style={{ minHeight: 36 }}
            />
            {hq && (
              <Select
                aria-label="Lọc chi nhánh"
                value={branchId}
                onChange={(v) => {
                  setBranchId(v);
                  resetPaging();
                }}
                style={{ minWidth: 190, minHeight: 36 }}
                options={branchOptions}
              />
            )}
            <Tooltip title={`Xuất CSV đối soát ngày ${dateLabel} (UTF-8, mở được Excel)`}>
              <Button
                icon={<DownloadSimple size={16} />}
                loading={csvDownloading}
                onClick={() => void downloadCsv()}
              >
                Xuất CSV
              </Button>
            </Tooltip>
          </Space>
        }
      />
      <PageBody>
        {/* AntD cắt cụt tiêu đề card (overflow:hidden, nowrap) — cho phép xuống dòng để
            bộ lọc/mô tả không bị ẩn trên màn nhỏ. */}
        <style>{`
          .recon-card .ant-card-head { height: auto; padding-block: 12px; }
          .recon-card .ant-card-head-title { white-space: normal; overflow: visible; text-overflow: clip; }
        `}</style>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 12,
            marginBottom: 18,
          }}
        >
          <Tile
            label="Tỷ lệ tự quét gắn cờ"
            value={stats ? `${stats.flaggedRatePercent}%` : '—'}
            hint={stats ? `${stats.selfScanFlagged}/${stats.selfScanTotal} hóa đơn` : 'Đang tải…'}
            danger={stats?.overThreshold}
          />
          <Tile
            label="Giao dịch cần xem lại"
            value={String(reviewCount)}
            hint="Chờ xác nhận và đảo điểm"
            danger={reviewCount > 0}
          />
          <Tile
            label="Doanh thu thành viên"
            value={dayEnd ? `${VND.format(dayEnd.totals.acceptedAmount)}đ` : '—'}
            hint={dayEnd ? `${VND.format(dayEnd.totals.acceptedScans)} hóa đơn tích điểm` : 'Đang tải…'}
            tooltip={`Tổng tiền hóa đơn có mã thành viên ngày ${dateLabel}. Đây không phải tổng doanh thu quán.`}
          />
          {/* Giữ chỗ cho đối chiếu doanh thu POS (Phase 2). Mờ vì Phase 1 độc lập POS (ADR-004) —
              chưa có nguồn số; xóa hẳn thì mất chỗ đã thiết kế trong prototype cho tính năng này. */}
          <Tile
            label="Doanh thu POS đối chiếu"
            value="—"
            hint="Nhập tay từ báo cáo chi nhánh"
            muted
            tooltip="Cần tổng doanh thu quán từ máy tính tiền để đối chiếu. Hệ thống chưa lấy được số này."
          />
          <Tile
            label="Đối soát điểm hai phía"
            value={dayEnd ? (dayEnd.pointsMatch ? 'Khớp' : 'Lệch') : '—'}
            hint={
              dayEnd
                ? `Hóa đơn ${VND.format(dayEnd.totals.pointsAwardedGross)} · sổ cái ${VND.format(dayEnd.totals.ledgerEarn)}`
                : 'Đang tải…'
            }
            danger={dayEnd ? !dayEnd.pointsMatch : false}
            tooltip="Điểm cộng phía hóa đơn (số gộp) so với tổng bút toán Earn của sổ cái. Lệch là dấu hiệu cần soát."
          />
        </div>

        {/* Hàng chờ xác nhận khách tự quét (chỉ quản lý chi nhánh) */}
        <Card
          className="recon-card"
          styles={{ body: { padding: 0 } }}
          style={{ borderRadius: tokens.radius.card, marginBottom: 18 }}
          title={
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Giao dịch cần xem lại</div>
              <div style={{ fontSize: 12.5, fontWeight: 400, color: tokens.color.ink2 }}>
                Hóa đơn khách tự quét đang chờ xác nhận. Xử lý hết trước khi chốt sổ ngày.
              </div>
            </div>
          }
          extra={!canConfirmPending ? <HintToggle hint={hqQueueHint} /> : null}
        >
          {!canConfirmPending && (
            <HintBanner
              hint={hqQueueHint}
              banner
              message="Tài khoản hội sở không gắn chi nhánh nên không mở được hàng chờ xác nhận. Dùng App nhân viên tại quầy, hoặc đăng nhập quản lý chi nhánh."
            />
          )}
          {canConfirmPending && pendingQuery.isError && (
            <div style={{ padding: 16 }}>
              <ErrorState error={pendingQuery.error} onRetry={() => void pendingQuery.refetch()} />
            </div>
          )}
          {canConfirmPending && pendingQuery.isSuccess && (
            <DataTable toolbar={false}
              rowKey="billScanId"
              size="small"
              columns={scanColumns}
              dataSource={pending}
              pagination={false}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'Không có hóa đơn nào đang chờ xác nhận.' }}
            />
          )}
        </Card>

        {/* Danh sách hóa đơn quét — xem lại theo ngày (S3-BE-07) */}
        <Card
          className="recon-card"
          styles={{ body: { padding: 0 } }}
          style={{ borderRadius: tokens.radius.card, marginBottom: 18 }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Danh sách hóa đơn quét</div>
                <div style={{ fontSize: 12.5, fontWeight: 400, color: tokens.color.ink2 }}>
                  Xem lại mọi hóa đơn đã quét ngày {dateLabel}. Đổi ngày ở trên để xem ngày cũ.
                </div>
              </div>
              <div style={{ flex: 1 }} />
              <Segmented
                value={scanSource}
                onChange={(v) => {
                  setScanSource(v as 'all' | 'Staff' | 'Member');
                  resetPaging();
                }}
                options={[
                  { label: 'Tất cả', value: 'all' },
                  { label: 'Nhân viên', value: 'Staff' },
                  { label: 'Khách tự quét', value: 'Member' },
                ]}
              />
              <Select
                aria-label="Lọc trạng thái hóa đơn"
                placeholder="Mọi trạng thái"
                allowClear
                value={scanStatus}
                onChange={(v) => {
                  setScanStatus(v);
                  resetPaging();
                }}
                style={{ minWidth: 160 }}
                options={Object.entries(BILL_STATUS).map(([value, meta]) => ({ value, label: meta.label }))}
              />
              <Checkbox
                checked={onlyFlagged}
                onChange={(e) => {
                  setOnlyFlagged(e.target.checked);
                  resetPaging();
                }}
              >
                Chỉ dòng có cờ
              </Checkbox>
            </div>
          }
        >
          {billScansQuery.isError && (
            <div style={{ padding: 16 }}>
              <ErrorState error={billScansQuery.error} onRetry={() => void billScansQuery.refetch()} />
            </div>
          )}
          {!billScansQuery.isError && (
            <>
              <DataTable
                onReload={() => void billScansQuery.refetch()}
                searchPlaceholder="Tìm hóa đơn"
                rowKey="id"
                size="small"
                loading={billScansQuery.isFetching}
                columns={billScanColumns}
                dataSource={billScans}
                pagination={false}
                toolbar={false}
                scroll={{ x: 'max-content' }}
                onRow={(row) =>
                  visibleFlags(row.flags).some((c) => flagMeta(c).tone === 'danger')
                    ? { style: { background: tokens.color.danger50 } }
                    : {}
                }
                locale={{ emptyText: 'Không có hóa đơn nào trong kỳ đã chọn.' }}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  rowGap: 8,
                  flexWrap: 'wrap',
                  padding: '12px 16px',
                  borderTop: `1px solid ${tokens.color.line}`,
                }}
              >
                <span style={{ fontSize: 12.5, color: tokens.color.ink3 }}>
                  Trang {pageIndex + 1} · {billScans.length} dòng
                  {billScansQuery.data ? ` · ${billScansQuery.data.flaggedCount} có cờ trong kỳ` : ''}
                </span>
                <div style={{ flex: 1 }} />
                <Button size="small" disabled={pageIndex === 0} onClick={() => setPageIndex((i) => Math.max(0, i - 1))}>
                  Trước
                </Button>
                <Button
                  size="small"
                  disabled={!billScansQuery.data?.nextCursor}
                  onClick={() => {
                    const next = billScansQuery.data?.nextCursor ?? null;
                    if (!next) return;
                    setCursorStack((s) => (pageIndex + 1 < s.length ? s : [...s, next]));
                    setPageIndex((i) => i + 1);
                  }}
                >
                  Sau
                </Button>
              </div>
            </>
          )}
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 18 }}>
          <Card
            className="recon-card"
            styles={{ body: { padding: 0 } }}
            style={{ borderRadius: tokens.radius.card }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>Yêu cầu đảo điểm chờ duyệt</div>
                  <div style={{ fontSize: 12.5, fontWeight: 400, color: tokens.color.ink2 }}>
                    Cần hai cấp: quản lý chi nhánh tạo, Super Admin duyệt. Hai cấp phải là hai người.
                  </div>
                </div>
                <Segmented
                  style={{ flexShrink: 0 }}
                  options={[
                    { label: 'Chờ duyệt', value: 'Pending' },
                    { label: 'Tất cả', value: 'all' },
                  ]}
                  value={reversalFilter}
                  onChange={(value) => setReversalFilter(value as 'Pending' | 'all')}
                />
              </div>
            }
          >
            {reversalsQuery.isPending && (
              <div style={{ padding: 16 }}>
                <LoadingState />
              </div>
            )}
            {reversalsQuery.isError && (
              <div style={{ padding: 16 }}>
                <ErrorState error={reversalsQuery.error} onRetry={() => void reversalsQuery.refetch()} />
              </div>
            )}
            {reversalsQuery.isSuccess && (
              <DataTable toolbar={false}
                rowKey="id"
                size="small"
                columns={reversalColumns}
                dataSource={reversals}
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: REVERSAL_PAGE_SIZE,
                  showSizeChanger: false,
                  hideOnSinglePage: true,
                  size: 'small',
                }}
                locale={{
                  emptyText:
                    reversalFilter === 'Pending'
                      ? 'Không có yêu cầu đảo điểm đang chờ.'
                      : 'Chưa có yêu cầu đảo điểm.',
                }}
              />
            )}
          </Card>

          <Card
            className="recon-card"
            style={{ borderRadius: tokens.radius.card }}
            title={
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>
                  Đối soát cuối ngày {dateLabel}
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 400, color: tokens.color.ink2 }}>
                  Phía hóa đơn so với phía sổ cái điểm
                </div>
              </div>
            }
          >
            {dayEndQuery.isError ? (
              <ErrorState error={dayEndQuery.error} onRetry={() => void dayEndQuery.refetch()} />
            ) : dayEndQuery.isPending ? (
              <LoadingState rows={4} />
            ) : dayEnd ? (
              <>
                <Alert
                  type={dayEnd.pointsMatch ? 'success' : 'warning'}
                  showIcon
                  icon={
                    dayEnd.pointsMatch ? (
                      <CheckCircle size={18} weight="fill" aria-hidden />
                    ) : (
                      <WarningCircle size={18} weight="fill" aria-hidden />
                    )
                  }
                  message={dayEnd.pointsMatch ? 'Điểm hai phía khớp' : 'Điểm hai phía LỆCH'}
                  description={
                    dayEnd.pointsMatch
                      ? 'Điểm cộng trên hóa đơn khớp tổng bút toán Earn của sổ cái.'
                      : 'Số điểm trên hóa đơn không khớp sổ cái — cần soát trước khi chốt.'
                  }
                  style={{ marginBottom: 12 }}
                />
                <ReconRow label="Tổng hóa đơn" value={VND.format(dayEnd.totals.totalScans)} />
                <ReconRow label="Đã chấp nhận" value={VND.format(dayEnd.totals.acceptedScans)} />
                <ReconRow label="Nhập tay" value={VND.format(dayEnd.totals.manualScans)} />
                <ReconRow label="Gắn cờ" value={VND.format(dayEnd.totals.flaggedScans)} danger={dayEnd.totals.flaggedScans > 0} />
                <ReconRow label="Chờ xác nhận" value={VND.format(dayEnd.totals.pendingConfirmationScans)} />
                <ReconRow label="Đảo điểm chờ duyệt" value={VND.format(dayEnd.totals.reversalPendingScans)} />
                <ReconRow label="Doanh thu thành viên" value={`${VND.format(dayEnd.totals.acceptedAmount)}đ`} />
                <ReconRow label="Điểm phát (hóa đơn)" value={VND.format(dayEnd.totals.pointsAwardedGross)} />
                <ReconRow label="Điểm phát (sổ cái)" value={VND.format(dayEnd.totals.ledgerEarn)} />
                <Button
                  block
                  icon={<DownloadSimple size={16} aria-hidden />}
                  loading={csvDownloading}
                  onClick={() => void downloadCsv()}
                  style={{ marginTop: 12 }}
                >
                  Xuất CSV ngày {date.format('DD/MM')}
                </Button>
              </>
            ) : null}
          </Card>
        </div>

        <Typography.Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 20 }}>
          Ảnh hóa đơn mở bằng link hạn 5 phút; mỗi lần xem được ghi nhật ký. Màn tự làm mới mỗi 30 giây.
          Đối soát cuối ngày và xuất CSV lấy theo ngày đang chọn. "Doanh thu quán" và "%
          phủ thành viên" cần dữ liệu từ máy tính tiền nên chưa có.
        </Typography.Paragraph>
      </PageBody>

      {/* Xem ảnh hóa đơn ngay trong app (fullscreen, có zoom/xoay), không mở tab mới. */}
      <Image
        style={{ display: 'none' }}
        src={previewUrl ?? ''}
        preview={{
          visible: previewUrl !== null,
          onVisibleChange: (visible) => {
            if (!visible) setPreviewUrl(null);
          },
        }}
      />

      <BillScanDetailModal
        scan={detailScan}
        onClose={() => setDetailScan(null)}
        onViewImage={(id) => void openImage(id)}
      />

      <RejectScanModal
        target={rejectingScan}
        submitting={decideMutation.isPending}
        onCancel={() => setRejectingScan(null)}
        onSubmit={(reason) => {
          if (rejectingScan) {
            decideMutation.mutate({ id: rejectingScan.id, approve: false, note: reason });
          }
        }}
      />

      <RejectReversalModal
        item={rejectingReversal}
        submitting={rejectReversalMutation.isPending}
        onCancel={() => setRejectingReversal(null)}
        onSubmit={(note) => {
          if (rejectingReversal) {
            rejectReversalMutation.mutate({ id: rejectingReversal.id, note });
          }
        }}
      />
    </>
  );
}

/** Một dòng số trong bảng đối soát cuối ngày. */
function ReconRow({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        padding: '7px 0',
        borderTop: `1px solid ${tokens.color.line}`,
        fontSize: 13,
      }}
    >
      <span style={{ color: tokens.color.ink2 }}>{label}</span>
      <b style={{ fontVariantNumeric: 'tabular-nums', color: danger ? tokens.color.danger : tokens.color.ink }}>
        {value}
      </b>
    </div>
  );
}

/** Chip lý do gắn cờ — mỗi mã một chip riêng (BR-210). */
function FlagChips({ flags }: { flags: string[] }) {
  const visible = visibleFlags(flags);
  if (visible.length === 0) {
    return <StatusTag color="default">Chờ xác nhận</StatusTag>;
  }
  return (
    <Space size={[4, 4]} wrap>
      {visible.map((code) => {
        const meta = flagMeta(code);
        return (
          <StatusTag
            key={code}
            color={meta.tone === 'danger' ? 'error' : meta.tone === 'warn' ? 'warning' : 'blue'}
          >
            {meta.label}
          </StatusTag>
        );
      })}
    </Space>
  );
}
